import "ol/ol.css";
import "./style.css";
import { Map, View } from "ol";
import { Control, defaults } from "ol/control";
import { fromLonLat } from "ol/proj";

import { AzureLoginControl } from "./controls/AzureLoginControl";

import { configureMonkeyFetch } from "./auth/monkeyFetch";
import { addSDOLayersAndControls } from "./config/SDOLayersAndControls";
import { addLatLongControl } from "./config/LatLongControl";
import { addBoundaryCodesControl } from "./config/BoundaryCodesControl";
import { addDateInputControl } from "./config/DateInputControl";
import { addHeatmapLayersAndControls } from "./config/HeatmapLayersAndControls";
import { addMomaLayersAndControls } from "./config/MomaLayersAndControls";
import { addGeocoderControl } from "./config/GeocoderControl";
import { addBasemapLayersAndControls } from "./config/BasemapLayersAndControls";
import { addWKTLayerAndControl } from "./config/WKTLayerAndControl";
import { addDrawControl } from "./config/DrawAndControl";
import LayerGroup from "ol/layer/Group";
import { addLayersAndControls } from "./config/LayersAndControls";
import { addProductControl } from "./config/ProductAndControl";
import { addFeatureInfoControl } from "./config/FeatureInfoControl";
import { installMemberHover } from "./featureinfo/memberHover.js";
import { DrawerControl } from "./controls/DrawerControl";
import { createSection } from "./sidebar/createSection";
import { loadCollapsed, saveCollapsed } from "./sidebar/sectionState.mjs";
import { ignoreHoverOverControls } from "./util/hoverOverControls.mjs";
import {
  getViewFromURL,
  registerMap,
  startSync,
} from "./state/URLStateManager";

const urlView = getViewFromURL();
var view = new View({
  center: urlView ? urlView.center : fromLonLat([7.233208, 50.3750669]),
  zoom: urlView ? urlView.zoom : 8,
});

// monkeypatch fetch to use the flatgeobuf library
configureMonkeyFetch();

const azureLoginControl = new AzureLoginControl();

// Left Generic Controls
export const leftControlDiv = document.createElement("div");
leftControlDiv.id = "controls-left";
leftControlDiv.className = "controls-left--sections";
const leftControl = new Control({
  element: leftControlDiv,
});

// Sidebar sections (created before login, so the controls that need no token can
// fill Place and Data right away) and the footer pinned under them.
function browserStorage() {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}
const collapsedSections = loadCollapsed(browserStorage());
const sectionsDiv = document.createElement("div");
sectionsDiv.className = "sidebar-sections";
const sidebarFooter = document.createElement("div");
sidebarFooter.className = "sidebar-footer";
leftControlDiv.append(sectionsDiv, sidebarFooter);
// Place is always open: no header to collapse it.
// Data and overlays share one section: the product/dates row, then FCD, MoMa, SDO.
const [placeBody, dataBody, layersBody] = [
  ["place", "Place", false],
  ["data-overlays", "Data & overlays", true],
  ["layers", "Layers", true],
].map(([id, title, collapsible]) => {
  const { element, body } = createSection(document, {
    id,
    title,
    collapsible,
    collapsed: collapsible && collapsedSections.has(id),
    onToggle: (sectionId, collapsed) => {
      if (collapsed) collapsedSections.add(sectionId);
      else collapsedSections.delete(sectionId);
      saveCollapsed(browserStorage(), collapsedSections);
    },
  });
  sectionsDiv.append(element);
  return body;
});

const vectorFeaturesControlDiv = document.createElement("div");
vectorFeaturesControlDiv.className = "vector-feature";
const vectorFeaturesControl = new Control({
  element: vectorFeaturesControlDiv,
  target: layersBody,
});
const map = new Map({
  target: "map",
  layers: [],
  view: view,
  controls: defaults({
    zoom: false,
    attribution: false,
    rotate: false,
  }).extend([azureLoginControl, leftControl]),
});
// First pointermove listener: features behind the sidebar are not hovered.
ignoreHoverOverControls(map);
registerMap(map);
const heatmapLayerGroup = new LayerGroup();
addLatLongControl(map, view, placeBody);
addBoundaryCodesControl(map, view, placeBody, azureLoginControl);
// Data: the dates row; after login the product box goes above it.
const dataRow = document.createElement("div");
dataRow.className = "sidebar-data-row";
dataBody.append(dataRow);
const dateInputControl = addDateInputControl(map, dataRow);
azureLoginControl.once("propertychange", function () {
  addBasemapLayersAndControls(map, azureLoginControl);

  const featureInfoControl = addFeatureInfoControl(map);
  installMemberHover(map);
  map.addControl(new DrawerControl());
  map.addLayer(heatmapLayerGroup);
  addHeatmapLayersAndControls(
    map,
    azureLoginControl,
    dateInputControl,
    dataBody,
    heatmapLayerGroup,
    featureInfoControl,
  );
  addMomaLayersAndControls(map, dataBody);
  // SDO
  addSDOLayersAndControls(map, azureLoginControl, dateInputControl, dataBody);
  // vector
  map.addControl(vectorFeaturesControl);
  const wktInputControl = addWKTLayerAndControl(
    map,
    sidebarFooter,
    view,
    azureLoginControl,
  )[1];
  // Drawing toolbar sits directly above the WKT box, both pinned in the footer.
  addDrawControl(map, sidebarFooter, wktInputControl);
  addGeocoderControl(map);
  addLayersAndControls(
    map,
    azureLoginControl,
    featureInfoControl,
    vectorFeaturesControlDiv,
  );

  addProductControl(map, dataBody);

  // Start URL state sync after all controls are registered
  startSync();
});
